import type { PenStrokeCanvasObject } from "@refinement-canvas/domain";
import {
  PEN_HIT_MARGIN_SCREEN_PX,
  PEN_SELECTION_RING_SCREEN_PX,
} from "../../../lib/pen-stroke-hit";
import { ZOOM_MIN } from "../../../lib/canvas-coordinates";
import { useCanvasStore } from "../store/canvas-store";

// 選択輪郭のマスクに使う 2 つの線幅（画面上の px）。
// 外側 = マージン + 輪郭線の太さ、内側 = マージンのみ。差分がリング（選択輪郭）になる。
// stroke は芯の両側へ均等に太るため、いずれも「膨張量 × 2」を線幅として与える。
const SELECTION_OUTER_WIDTH_PX = (PEN_HIT_MARGIN_SCREEN_PX + PEN_SELECTION_RING_SCREEN_PX) * 2;
const SELECTION_INNER_WIDTH_PX = PEN_HIT_MARGIN_SCREEN_PX * 2;
// mask 領域へ足す追加の余白（画面上の px）。マスク境界での切れを防ぐための保険。
const SELECTION_MASK_EXTRA_PAD_PX = 2;

interface Props {
  readonly object: PenStrokeCanvasObject;
  /** アウトラインの path 文字列。呼び出し側で getSharedStrokePath により算出して渡す。 */
  readonly path: string;
  /** 輪郭の色。自分の選択は var(--color-selection)、他参加者はその参加者の色。 */
  readonly color: string;
  /** <mask> の id。同一文書内で一意にする（自分用と他参加者用で接頭辞を分ける）。 */
  readonly maskId: string;
  /** 輪郭 <path> の data-testid。 */
  readonly testId: string;
}

/**
 * 選択中のペンストロークの輪郭線。自分の選択表示と他参加者の選択表示で共有する。
 *
 * 同一のアウトライン d を 2 通りの線幅で膨らませた領域の差分（外側 - 内側）をマスクで抜き、
 * 「膨張シルエットの外周」だけを描く。自己交差や近接で膨張領域が重なった箇所に生じる内部の
 * 線は、内側領域（黒 = 抜く領域）に必ず含まれるため消える。両者が同一の d で、内側の線幅が
 * 外側より小さいことが、この性質を保証する条件になる。
 *
 * mask の描画領域（x / y / width / height）は SVG 属性のため calc() を使えず、ズーム倍率から
 * 算出する必要がある。選択中のストロークだけがズームを購読するよう、独立したコンポーネント
 * へ切り出している（非選択のストロークはズーム変更で再レンダーしない）。
 */
export function PenStrokeSelectionOutline({
  object,
  path,
  color,
  maskId,
  testId,
}: Props): JSX.Element {
  const zoom = useCanvasStore((s) => s.view.zoom);
  const inv = 1 / Math.max(zoom, ZOOM_MIN);
  // 膨張分がマスク領域の外へ出て輪郭が切れないよう、アウトラインが外接矩形の端へ接している
  // 場合を見込んで余裕を取る（広めに取っても描画結果は変わらない）。thickness は SVG 属性へ
  // そのまま載るため、domain 側の computePenStrokeFrame と同じ規則で非数・負値を弾く。
  const safeThickness = Number.isFinite(object.thickness) ? Math.max(0, object.thickness) : 0;
  const pad =
    (PEN_HIT_MARGIN_SCREEN_PX + PEN_SELECTION_RING_SCREEN_PX + SELECTION_MASK_EXTRA_PAD_PX) * inv +
    safeThickness;
  const outerWidth = `calc(${SELECTION_OUTER_WIDTH_PX}px * var(--canvas-inv-zoom, 1))`;
  const innerWidth = `calc(${SELECTION_INNER_WIDTH_PX}px * var(--canvas-inv-zoom, 1))`;
  return (
    <>
      {/* 複製ドラッグのゴーストは同一文書へ cloneNode されるため、mask の id が重複しないよう
          use-object-drag 側がこの属性を目印にゴーストから取り除く。 */}
      <defs data-canvas-selection-ui="true">
        <mask
          id={maskId}
          maskUnits="userSpaceOnUse"
          x={object.x - pad}
          y={object.y - pad}
          width={object.w + pad * 2}
          height={object.h + pad * 2}
        >
          {/* 白 = 残す領域。ストローク本体をマージン + 輪郭線の太さだけ膨らせた形。 */}
          <path
            d={path}
            fill="#fff"
            stroke="#fff"
            strokeLinejoin="round"
            strokeLinecap="round"
            style={{ strokeWidth: outerWidth }}
          />
          {/* 黒 = 抜く領域。マージン分だけ膨らせた形を抜き、外周のリングだけを残す。 */}
          <path
            d={path}
            fill="#000"
            stroke="#000"
            strokeLinejoin="round"
            strokeLinecap="round"
            style={{ strokeWidth: innerWidth }}
          />
        </mask>
      </defs>
      <path
        data-canvas-selection-ui="true"
        data-testid={testId}
        d={path}
        fill={color}
        stroke={color}
        strokeLinejoin="round"
        strokeLinecap="round"
        mask={`url(#${maskId})`}
        pointerEvents="none"
        style={{ strokeWidth: outerWidth }}
      />
    </>
  );
}
